import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRedirectPending } from '@/hooks/useRedirectPending';
import { useNavigate } from '@tanstack/react-router';
import {
	confirmOneOffPaymentMePaymentsPostMutation,
	createCreditCheckoutCreditsCheckoutPostMutation,
	getBundlePropertyBundlesBundleIdGetOptions,
	getCreditBalanceCreditsBalanceGetQueryKey,
	getSavedPaymentMethodMePaymentMethodGetOptions,
} from '@/client/@tanstack/react-query.gen';
import { OneOffPaymentRequest, PropertyBundle, SavedPaymentMethodResponse } from '@/client';
import { useAuth } from '@/context/AuthContext.tsx';
import { useHouseContext } from '@/context/HouseContext.tsx';
import { Routes } from '@/routes';

const ONE_OFF_PURCHASE_TYPE = 'property_credits';
const PAYMENT_SUCCESS_PATH = Routes.PAYMENT_SUCCESS;
const PAYMENT_JUST_CONFIRMED_KEY = 'paymentJustConfirmed';

export const PAYMENT_STATUS = {
	IDLE: 'idle',
	SUCCEEDED: 'succeeded',
} as const;

export const CHECKOUT_RESULT = {
	SUCCEEDED: 'succeeded',
	REQUIRES_ACTION: 'requires_action',
	FAILED: 'failed',
} as const;

type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];
export type ConfirmCheckoutResult = (typeof CHECKOUT_RESULT)[keyof typeof CHECKOUT_RESULT];

/**
 * Hook for handling credit-based checkout flow
 * Manages Stripe checkout session creation for purchasing property credits
 * Now includes terms compliance check to prevent unauthorized payment access
 */
export const useCreditCheckout = () => {
	const { isTermsCompliant } = useAuth();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { resetSelectedHouse } = useHouseContext();
	const selectedBundleRef = useRef<string | null>(null);
	// Guards against duplicate initiation against the same instance — now that onboarding shares one
	// checkout, the entry gate and a fast step-1 Next click can both call initiateCheckout. Stays true
	// for the whole flow (preparing → dialog open / redirect) and only releases on a terminal outcome.
	const initiationInFlightRef = useRef(false);
	const [savedCard, setSavedCard] = useState<SavedPaymentMethodResponse | null>(null);
	const [pendingPayment, setPendingPayment] = useState<OneOffPaymentRequest | null>(null);
	const [pendingBundle, setPendingBundle] = useState<PropertyBundle | null>(null);
	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
	const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(PAYMENT_STATUS.IDLE);
	// Tracks the async prep work (saved-card + bundle lookup) before a checkout decision is made,
	// which is otherwise invisible to the loading state because it's reads, not mutations.
	const [isPreparingCheckout, setIsPreparingCheckout] = useState(false);
	// Stays true once we commit to leaving the page for Stripe. Never reset on the happy path:
	// it keeps the spinner up through the browser navigation so there's no spinner-less dead time.
	const { isRedirecting, setIsRedirecting } = useRedirectPending();

	const { mutateAsync: createCreditCheckout, isPending: isCreatingCheckout } = useMutation(
		createCreditCheckoutCreditsCheckoutPostMutation(),
	);
	const { mutateAsync: confirmOneOffPayment, isPending: isConfirmingPayment } = useMutation(
		confirmOneOffPaymentMePaymentsPostMutation(),
	);

	const runCheckoutRedirect = async (bundleId: string) => {
		setIsRedirecting(true);
		try {
			const session = await createCreditCheckout({ body: { bundle_id: bundleId } });
			if (session.checkout_url) {
				window.location.href = session.checkout_url;
				return;
			}
			// No URL to navigate to — release the loading state so the UI isn't stuck spinning.
			setIsRedirecting(false);
			initiationInFlightRef.current = false;
		} catch (error) {
			setIsRedirecting(false);
			initiationInFlightRef.current = false;
			throw error;
		}
	};

	const initiateCheckout = async (bundleId: string) => {
		if (!isTermsCompliant) {
			console.warn('Cannot initiate checkout: terms not accepted');
			return;
		}
		if (initiationInFlightRef.current) {
			console.warn('Checkout already in progress — ignoring duplicate initiation');
			return;
		}
		initiationInFlightRef.current = true;
		selectedBundleRef.current = bundleId;
		setIsPreparingCheckout(true);

		try {
			const [savedPaymentMethod, selectedBundle] = await Promise.all([
				queryClient.fetchQuery(getSavedPaymentMethodMePaymentMethodGetOptions()),
				queryClient.fetchQuery(
					getBundlePropertyBundlesBundleIdGetOptions({
						path: { bundle_id: bundleId },
					}),
				),
			]);

			if (!savedPaymentMethod.has_saved_card) {
				await runCheckoutRedirect(bundleId);
				return;
			}

			if (selectedBundle.price_amount_cents == null) {
				console.error('Bundle price is missing for saved-card confirmation flow');
				await runCheckoutRedirect(bundleId);
				return;
			}

			const requestBody: OneOffPaymentRequest = {
				currency: selectedBundle.currency ?? undefined,
				description: selectedBundle.name,
				purchase_type: ONE_OFF_PURCHASE_TYPE,
				bundle_id: selectedBundle.id,
				return_url: `${window.location.origin}${PAYMENT_SUCCESS_PATH}`,
				expected_price_cents: selectedBundle.price_amount_cents,
			};

			setSavedCard(savedPaymentMethod);
			setPendingBundle(selectedBundle);
			setPendingPayment(requestBody);
			setPaymentStatus(PAYMENT_STATUS.IDLE);
			setIsConfirmationOpen(true);
		} catch (error) {
			console.error('Unable to load saved payment method:', error);
			await runCheckoutRedirect(bundleId);
		} finally {
			setIsPreparingCheckout(false);
		}
	};

	const closeConfirmation = () => {
		setIsConfirmationOpen(false);
		setPendingPayment(null);
		setPendingBundle(null);
		initiationInFlightRef.current = false;
	};

	const confirmCheckout = async (): Promise<ConfirmCheckoutResult> => {
		if (!pendingPayment) {
			return CHECKOUT_RESULT.FAILED;
		}

		try {
			const paymentResponse = await confirmOneOffPayment({ body: pendingPayment });

			if (paymentResponse.status === CHECKOUT_RESULT.REQUIRES_ACTION && paymentResponse.redirect_url) {
				setIsRedirecting(true);
				window.location.href = paymentResponse.redirect_url;
				return CHECKOUT_RESULT.REQUIRES_ACTION;
			}

			if (paymentResponse.status !== CHECKOUT_RESULT.SUCCEEDED) {
				console.error('Payment failed with status:', paymentResponse.status);
				return CHECKOUT_RESULT.FAILED;
			}

			setPaymentStatus(PAYMENT_STATUS.SUCCEEDED);
			setIsConfirmationOpen(false);
			setPendingPayment(null);
			setPendingBundle(null);
			initiationInFlightRef.current = false;

			resetSelectedHouse();
			sessionStorage.setItem(PAYMENT_JUST_CONFIRMED_KEY, 'true');
			await queryClient.invalidateQueries({ queryKey: getCreditBalanceCreditsBalanceGetQueryKey() });
			navigate({ to: PAYMENT_SUCCESS_PATH });
			return CHECKOUT_RESULT.SUCCEEDED;
		} catch (error) {
			const status =
				(error as { status?: number; response?: { status?: number } })?.response?.status ??
				(error as { status?: number })?.status;

			if (status === 400 && selectedBundleRef.current) {
				await runCheckoutRedirect(selectedBundleRef.current);
				return CHECKOUT_RESULT.FAILED;
			}

			console.error('Confirm payment failed:', error);
			return CHECKOUT_RESULT.FAILED;
		}
	};

	return {
		initiateCheckout,
		confirmCheckout,
		cancelCheckout: closeConfirmation,
		isConfirmationOpen,
		savedCard,
		pendingPayment,
		pendingBundle,
		paymentStatus,
		isProcessingCheckout: isCreatingCheckout || isConfirmingPayment || isPreparingCheckout || isRedirecting,
	};
};
